/**
 * Shared text formatting utilities for GMS.
 * Ensures consistent data entry across all modules.
 */

/** Convert ISO/UTC datetime string to IST DD-MM-YYYY hh:mm AM/PM */
export function formatDateTimeIST(val) {
  if (!val) return "";
  try {
    const dt = new Date(val);
    if (isNaN(dt.getTime())) return String(val);
    return dt.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return String(val);
  }
}

/** Convert ISO/UTC datetime string to IST DD-MM-YYYY */
export function formatDateIST(val) {
  if (!val) return "";
  try {
    const dt = new Date(val);
    if (isNaN(dt.getTime())) return String(val);
    return dt.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return String(val);
  }
}

/** Convert ISO/UTC datetime string to IST hh:mm AM/PM */
export function formatTimeIST(val) {
  if (!val) return "";
  try {
    const dt = new Date(val);
    if (isNaN(dt.getTime())) return String(val);
    return dt.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return String(val);
  }
}

/** Convert string to Title Case (proper case) */
export function toProperCase(str) {
  if (!str) return "";
  return str
    .toLowerCase()
    .replace(/(?:^|\s|[-/()])\w/g, (match) => match.toUpperCase());
}

/** Convert to UPPERCASE (for vehicle nos, AWB, challan, ref nos) */
export function toUpperCase(str) {
  if (!str) return "";
  return str.toUpperCase().trim();
}

/** Strip non-digit characters (for phone numbers) */
export function digitsOnly(str) {
  if (!str) return "";
  return str.replace(/\D/g, "").slice(0, 10);
}

/** Fields that should be proper-cased */
const PROPER_CASE_FIELDS = [
  "visitor_name", "employee_name", "driver_name", "person_name",
  "company", "company_from", "party_name", "party_company",
  "customer_name", "supplier", "sender", "recipient",
  "whom_to_meet", "handed_over_to", "issued_to", "authorized_by",
  "approved_by", "material_description", "purpose", "department",
  "key_name", "courier_company",
];

/** Fields that should be UPPERCASED */
const UPPER_FIELDS = [
  "vehicle_no", "awb_no", "erp_challan_no", "challan_no",
  "invoice_no", "seal_no", "ref_no", "transfer_note_no",
];

/** Fields that should only contain digits */
const DIGIT_FIELDS = [
  "contact_no", "mobile", "phone", "contact",
  "recipient_mobile",
];

/**
 * Format a form field value based on its key name.
 * Call this in setField or before submit.
 */
export function formatFieldValue(key, value) {
  if (typeof value !== "string") return value;
  const k = key.toLowerCase();
  if (PROPER_CASE_FIELDS.includes(k)) return toProperCase(value);
  if (UPPER_FIELDS.includes(k)) return toUpperCase(value);
  if (DIGIT_FIELDS.includes(k)) return digitsOnly(value);
  return value;
}

/**
 * Format an entire form object before submission.
 * Applies proper case, uppercase, and digit-only rules.
 */
export function formatFormData(formData) {
  const formatted = { ...formData };
  for (const [key, value] of Object.entries(formatted)) {
    if (typeof value === "string") {
      formatted[key] = formatFieldValue(key, value);
    }
    // Handle parties_list array
    if (key === "parties_list" && Array.isArray(value)) {
      formatted[key] = value.map((v) => (typeof v === "string" ? toProperCase(v) : v));
    }
  }
  return formatted;
}
